import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { BrandAuthConfig } from "@brand-auth/types";

/**
 * Auth provider for the funnel. Wraps Kinde's `KindeProvider` with the
 * per-application auth config resolved from D1 at runtime.
 *
 * Mount only AFTER the funnel has resolved the application's auth config.
 * Mounting with a wrong config poisons the Kinde session.
 */

const BrandAuthContext = createContext<BrandAuthConfig | null>(null);

export type BrandAuthProviderProps = {
  config: BrandAuthConfig;
  /**
   * Where the Kinde redirect lands. With query-param callback detection
   * this is typically the funnel root (`${origin}`).
   */
  redirectUri: string;
  /**
   * Optional logout target. Defaults to the config's `appOrigin`.
   */
  logoutUri?: string;
  /**
   * When true, Kinde stores the refresh token in localStorage so the
   * session survives full-page reloads. Required when the auth callback
   * does a full-page navigation (e.g. `window.location.replace`) because
   * in-memory tokens are lost on reload. Third-party cookie restrictions
   * in modern browsers also prevent silent iframe-based refresh when the
   * Kinde domain differs from the app domain.
   *
   * Default: `true` (always enabled).
   */
  useInsecureForRefreshToken?: boolean;
  children: ReactNode;
};

export const BrandAuthProvider = (props: BrandAuthProviderProps) => {
  const { config, redirectUri, logoutUri, useInsecureForRefreshToken, children } = props;
  const stableConfig = useMemo(
    () => config,
    [config.kindeDomain, config.kindeClientId, config.kindeOrgCode, config.appOrigin],
  );
  const insecureRefresh = useInsecureForRefreshToken ?? true;

  return (
    <BrandAuthContext.Provider value={stableConfig}>
      <KindeProvider
        domain={stableConfig.kindeDomain}
        clientId={stableConfig.kindeClientId}
        redirectUri={redirectUri}
        logoutUri={logoutUri ?? stableConfig.appOrigin}
        useInsecureForRefreshToken={insecureRefresh}
      >
        {children}
      </KindeProvider>
    </BrandAuthContext.Provider>
  );
};

/**
 * Read the active application's auth config. Throws if called outside a
 * `<BrandAuthProvider>`.
 */
export const useBrandAuthConfig = (): BrandAuthConfig => {
  const config = useContext(BrandAuthContext);
  if (!config) {
    throw new Error(
      "useBrandAuthConfig must be used within a <BrandAuthProvider>. " +
        "Mount the provider once the funnel has resolved its application's auth config.",
    );
  }
  return config;
};

/**
 * Non-throwing variant of {@link useBrandAuthConfig}. Returns `null` when
 * called outside a {@link BrandAuthProvider}.
 */
export const useBrandAuthConfigOptional = (): BrandAuthConfig | null => {
  return useContext(BrandAuthContext);
};
